class FreeShippingMeter extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.headingElement = this.querySelector('[data-reward-heading]');
        this.tiersElement = this.querySelector('[data-reward-tiers]');
        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.markersElement = this.querySelector('[data-reward-markers]');
        this.messageElement = this.querySelector('[data-shipping-message]');

        this.addEventListener('change', this.onCartChange.bind(this));
        this.initialize();
    }

    initialize() {
        Shopify.getCart((cart) => {
            this.cart = cart;
            this.render(cart);
        });
    }

    onCartChange() {
        this.initialize();
    }

    getConfig() {
        const rewards = window.cart_rewards || {};
        const legacyFreeShippingPrice = parseFloat(window.free_shipping_price) || 40;
        const legacyFreeShippingText = window.free_shipping_text || {};
        const fallbackTiers = [
            {
                price: legacyFreeShippingPrice,
                label: legacyFreeShippingText.free_shipping_message_4 || 'Free Delivery',
                icon: 'truck'
            }
        ];
        const tiers = (Array.isArray(rewards.tiers) && rewards.tiers.length ? rewards.tiers : fallbackTiers)
            .map((tier) => {
                const price = parseFloat(tier.price);

                return {
                    price,
                    cents: Math.max(0, Math.round(price * 100)),
                    label: tier.label || '',
                    icon: tier.icon || 'gift'
                };
            })
            .filter((tier) => tier.cents > 0 && tier.label)
            .sort((a, b) => a.cents - b.cents);

        return {
            heading: rewards.heading || 'Unlock more as you shop!',
            tiers,
            texts: {
                empty: rewards.texts?.empty || 'Rewards start at [reward_amount].',
                away: rewards.texts?.away || "You're [amount] away from [reward]!",
                unlockedAway: rewards.texts?.unlockedAway || "You've unlocked [unlocked]. You're [amount] away from [reward]!",
                complete: rewards.texts?.complete || "You've unlocked [reward]!"
            }
        };
    }

    render(cart) {
        const config = this.getConfig();

        if (!config.tiers.length || !this.progressBar || !this.progressMeter) return;

        const cartTotalCents = Number(cart.total_price) || 0;
        const maxTierCents = config.tiers[config.tiers.length - 1].cents;
        const progressPercent = maxTierCents > 0 ? Math.min(100, (cartTotalCents * 100) / maxTierCents) : 100;
        const nextTier = config.tiers.find((tier) => cartTotalCents < tier.cents);
        const unlockedTier = [...config.tiers].reverse().find((tier) => cartTotalCents >= tier.cents);

        if (this.headingElement) {
            this.headingElement.textContent = config.heading;
        }

        this.renderTierLabels(config.tiers, cartTotalCents, maxTierCents);
        this.renderMarkers(config.tiers, cartTotalCents, maxTierCents);

        this.progressMeter.style.width = `${progressPercent}%`;
        this.progressBar.setAttribute('aria-valuenow', String(Math.round(progressPercent)));

        if (this.messageElement) {
            this.messageElement.innerHTML = this.getMessage(config, cartTotalCents, nextTier, unlockedTier);
        }

        this.convertCurrency();
    }

    renderTierLabels(tiers, cartTotalCents, maxTierCents) {
        if (!this.tiersElement) return;

        this.tiersElement.innerHTML = tiers.map((tier, index) => {
            const position = this.getTierPosition(tier.cents, maxTierCents);
            const stateClass = cartTotalCents >= tier.cents ? ' is-unlocked' : '';

            return `
                <div class="cartRewards-tier cartRewards-tier--${index + 1}${stateClass}" style="--reward-position: ${position}%">
                    <div class="cartRewards-tierPrice">${this.formatMoney(tier.cents)}</div>
                    <div class="cartRewards-tierLabel">${this.escapeHtml(tier.label)}</div>
                </div>
            `;
        }).join('');
    }

    renderMarkers(tiers, cartTotalCents, maxTierCents) {
        if (!this.markersElement) return;

        this.markersElement.innerHTML = tiers.map((tier, index) => {
            const position = this.getTierPosition(tier.cents, maxTierCents);
            const isUnlocked = cartTotalCents >= tier.cents;
            const icon = this.getIconMarkup(isUnlocked ? 'check' : tier.icon);
            const label = isUnlocked ? `${tier.label} unlocked` : tier.label;

            return `
                <div class="cartRewards-marker cartRewards-marker--${index + 1}${isUnlocked ? ' is-unlocked' : ''}" style="--reward-position: ${position}%" aria-label="${this.escapeHtml(label)}">
                    <span class="cartRewards-markerIcon" aria-hidden="true">${icon}</span>
                </div>
            `;
        }).join('');
    }

    getMessage(config, cartTotalCents, nextTier, unlockedTier) {
        if (cartTotalCents === 0) {
            const firstTier = config.tiers[0];
            const text = config.texts.empty
                .replace('[reward_amount]', this.formatMoney(firstTier.cents))
                .replace('[reward]', this.escapeHtml(firstTier.label));

            return this.getMessageMarkup(firstTier.icon, text);
        }

        if (nextTier) {
            const remainingCents = nextTier.cents - cartTotalCents;
            const template = unlockedTier ? config.texts.unlockedAway : config.texts.away;
            const text = template
                .replace('[unlocked]', this.escapeHtml(unlockedTier?.label || ''))
                .replace('[amount]', this.formatMoney(remainingCents))
                .replace('[reward]', this.escapeHtml(nextTier.label));

            return this.getMessageMarkup(nextTier.icon, text);
        }

        const finalTier = config.tiers[config.tiers.length - 1];
        const text = config.texts.complete.replace('[reward]', this.escapeHtml(finalTier.label));

        return this.getMessageMarkup('check', text);
    }

    getMessageMarkup(icon, text) {
        return `
            <span class="cartRewards-messageIcon" aria-hidden="true">${this.getIconMarkup(icon)}</span>
            <span>${text}</span>
        `;
    }

    getTierPosition(tierCents, maxTierCents) {
        if (maxTierCents <= 0) return 0;

        return Math.max(0, Math.min(100, (tierCents * 100) / maxTierCents));
    }

    getIconMarkup(icon) {
        if (icon === 'check') {
            return '<svg aria-hidden="true" focusable="false" viewBox="0 0 512 512" class="icon icon-check"><path fill="currentColor" d="M435.848 83.466L172.804 346.51l-96.652-96.652c-4.686-4.686-12.284-4.686-16.971 0l-28.284 28.284c-4.686 4.686-4.686 12.284 0 16.971l133.421 133.421c4.686 4.686 12.284 4.686 16.971 0l299.813-299.813c4.686-4.686 4.686-12.284 0-16.971l-28.284-28.284c-4.686-4.686-12.284-4.686-16.97 0z"></path></svg>';
        }

        const template = this.querySelector(`[data-reward-icon-template="${icon}"]`);

        if (template) return template.innerHTML;

        return '';
    }

    formatMoney(cents) {
        if (window.Shopify?.formatMoney) {
            return Shopify.formatMoney(cents, window.money_format);
        }

        return `\u00a3${(cents / 100).toFixed(2)}`;
    }

    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    convertCurrency() {
        if ((window.show_multiple_currencies && typeof Currency !== 'undefined' && Currency.currentCurrency !== shopCurrency) || window.show_auto_currency) {
            Currency.convertAll(window.shop_currency, $('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
        }
    }
}

const defineFreeShippingMeter = () => {
    if (!customElements.get('free-shipping-component')) {
        customElements.define('free-shipping-component', FreeShippingMeter);
    }
};

if (document.readyState === 'loading') {
    window.addEventListener('load', defineFreeShippingMeter);
} else {
    defineFreeShippingMeter();
}
